import { useListFacilities } from "#hooks/adapters/useFacilities";
import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import useLocalization from "#src/hooks/useLocalization";
import { useStorageKey } from "#src/hooks/useStorageKey";
import {
  DataTablePanel,
  ItemAction,
  StorageKeys,
  useFilters,
} from "@validereinc/common-components";
import {
  AssetGroupAssetType,
  AssetType,
  FacilityType,
  SortDirection,
} from "@validereinc/domain";
import { toFlattenedObject } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React from "react";
import styles from "./FlowFormPanel.module.scss";

const cx = classNames.bind(styles);

export const FlowFacilitySelectionTablePanel = ({
  selectedAssets,
  addAssetToFlow,
  filterConfigStorageKey,
}: Pick<StorageKeys, "filterConfigStorageKey"> & {
  selectedAssets: Record<string, AssetGroupAssetType>;
  addAssetToFlow: (asset: AssetGroupAssetType, isUpstream: boolean) => void;
}) => {
  const [tableConfigStorageKey, tableStateStorageKey] = useStorageKey(
    ["table-configuration", "table-state"],
    "flow-facility-selection"
  );
  const { localize } = useLocalization();
  const [{ entity_type: _, ...filters }] = useFilters<any>(
    filterConfigStorageKey
  );
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "name",
      sortDirection: SortDirection.ASCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const { data, isLoading } = useListFacilities({
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: toFlattenedObject(filters),
  });

  const items = data?.data ?? [];

  const addOrigin = (item: FacilityType) => {
    addAssetToFlow({ ...item, asset_type: AssetType.FACILITY }, true);
  };

  const addDestination = (item: FacilityType) => {
    addAssetToFlow({ ...item, asset_type: AssetType.FACILITY }, false);
  };

  const getItemActions = ({ item }: { item: FacilityType }): ItemAction[] =>
    Object.keys(selectedAssets)?.includes(item.id)
      ? []
      : [
          {
            isOverflow: true,
            label: "Add as origin",
            buttonProps: {
              onClick: () => addOrigin(item),
            },
          },
          {
            isOverflow: true,
            label: "Add as destination",
            buttonProps: {
              onClick: () => addDestination(item),
            },
          },
        ];

  return (
    <div className={cx("streamSelectionContainer")}>
      <div className={cx("streamTableContainer")}>
        <DataTablePanel
          storageKey={tableConfigStorageKey}
          panelProps={{
            title: localize("facility"),
            description: `You may select up to one ${localize(
              "facility"
            )} as the origin and/or destination of your ${localize("flow")}.`,
          }}
          dataTableProps={{
            headers: [{ key: "name", label: `${localize("Facility")} Name` }],
            sorting: {
              sortBy: tableState.sortBy,
              sortDirection: tableState.sortDirection,
            },
            isLoading,
            items,
            getItemId: (item) => item.id,
            pagination: {
              page: tableState.page,
              pageSize: tableState.pageSize,
              total: data?.total_entries,
            },
            onSortChange: updateTableState,
            onPaginationChange: updateTableState,
            isFluid: false,
            getItemActions,
          }}
        />
      </div>
    </div>
  );
};
